import { useQuery } from "@tanstack/react-query";
import { documentKeys } from "./documents.keys";
import { useApi } from "../../api/ApiProvider";
import { DocumentDto } from "../../api-client";

// ============================================================================
// Types for OCR Extraction
// ============================================================================

/**
 * Field mapping from RxExtractionDto property names to OCR field names
 */
export type OcrFieldMapping = { [key: string]: string };

/**
 * Raw OCR Field data with confidence
 */
export interface OcrFieldData {
  confidence?: number;
  valueString?: string;
  valueInteger?: number;
  valueBoolean?: boolean;
  valueDate?: string;
  spans?: Array<{ offset: number; length: number }>;
  source?: string;
  type?: string;
}

/**
 * Raw OCR extraction structure
 */
export interface RawOcrExtraction {
  contents?: Array<{
    fields?: Record<string, OcrFieldData>;
    [key: string]: unknown;
  }>;
  analyzerId?: string;
  createdAt?: string;
  [key: string]: unknown;
}

// ============================================================================
// OCR Field Mapping Query (cached for 4 hours)
// ============================================================================

/**
 * Hook to fetch and cache the OCR field mapping.
 * This mapping connects RxExtractionDto field names to the raw OCR field names
 * for confidence lookups.
 * 
 * Cached for 4 hours since this mapping is static configuration.
 */
export const useOcrFieldMapping = () => {
  const { documents } = useApi();
  
  return useQuery({
    queryKey: documentKeys.ocrFieldMapping.all(),
    queryFn: () => documents.getOcrFieldMapping() as Promise<OcrFieldMapping>,
    meta: { errorMessage: "OCR-Feldzuordnung konnte nicht geladen werden" },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    gcTime: 4 * 60 * 60 * 1000, // keep in cache for 4 hours after last access
  });
};

// ============================================================================
// Inbox Documents Query
// ============================================================================

/**
 * Hook to fetch inbox documents for a location.
 * Returns documents that have inboxLocationId set but no patientId (unassigned).
 */
export const useInboxDocuments = (locationId: string | undefined) => {
  const { documents } = useApi();

  return useQuery({
    queryKey: documentKeys.inbox.forLocation(locationId || ""),
    queryFn: () => documents.getInboxDocuments(locationId!) as Promise<DocumentDto[]>,
    enabled: !!locationId,
    meta: { errorMessage: "Eingangskorb konnte nicht geladen werden" },
  });
};
